import { END_CUSTOMER_COUNTRY_CODES } from './end-customer-country-check.gen';

/**
 * The countries the End Customer widget offers, as ISO 3166-1 alpha-2 codes,
 * the alphabet cctool-go takes an end customer's country in (issue #619).
 *
 * The widget used to list legacy's `/apiv2/countries`, which is hard-coded
 * to FI, EE, GB, NO and SE with English names, so a handler could not pick
 * Spain for a Spanish payer. These are the 21 eurozone countries plus DK, GB,
 * NO and SE, the other countries the consoles offered: Finland first, then the
 * rest in the order of their Finnish names.
 */
const OFFERED_COUNTRY_CODES = [
  'FI',
  'NL', 'BE', 'BG', 'ES', 'IE', 'GB', 'IT', 'AT', 'GR', 'HR', 'CY', 'LV',
  'LT', 'LU', 'MT', 'NO', 'PT', 'FR', 'SE', 'DE', 'SK', 'SI', 'DK', 'EE',
] as const;

export interface CountryOption {
  readonly code: string;
  readonly name: string;
}

// The app's TypeScript lib (es2017) predates Intl.DisplayNames, which every
// browser the console supports has; this is the part of it used here.
interface RegionNames {
  of(code: string): string | undefined;
}
type RegionNamesConstructor = new (locales: string[], options: { type: 'region' }) => RegionNames;

const regionNames = new (Intl as unknown as { DisplayNames: RegionNamesConstructor }).DisplayNames(['fi'], {
  type: 'region',
});

/**
 * The Finnish name of an end customer's stored country, as console-svelte's
 * `countryName()` gives it. Only a code cctool-go takes as a country is named,
 * one off the list included (`END_CUSTOMER_COUNTRY_CODES`, generated from the
 * server's check, issue #626). Any other value shows as it is: one stored
 * before the server checked the country (`Finland`, `es`), and a code the
 * browser names but the server refuses (`UK`, which it would name as Great
 * Britain). No country shows nothing.
 */
export function countryName(code: string | null | undefined): string {
  if (!code) {
    return '';
  }
  if (!END_CUSTOMER_COUNTRY_CODES.includes(code)) {
    return code;
  }
  return regionNames.of(code) ?? code;
}

export const END_CUSTOMER_COUNTRIES: readonly CountryOption[] = OFFERED_COUNTRY_CODES.map((code) => ({
  code,
  name: countryName(code),
}));

/**
 * The options of a Country select for an end customer whose stored country
 * is `stored`: the list, plus the stored value at the end when it is not on
 * it, so the select shows what is stored and a save that leaves it alone
 * sends it back unchanged, which cctool-go takes from a record stored before
 * its check.
 */
export function endCustomerCountryOptions(stored: string | null | undefined): readonly CountryOption[] {
  if (!stored || END_CUSTOMER_COUNTRIES.some((c) => c.code === stored)) {
    return END_CUSTOMER_COUNTRIES;
  }
  return [...END_CUSTOMER_COUNTRIES, { code: stored, name: countryName(stored) }];
}
